import { createContext, useCallback, useState, type Dispatch, type SetStateAction, type ReactNode } from 'react';
import {
  FAR_TERRAIN_LENGTH_DEFAULT,
  getTargetRadiusMax,
  MID_TERRAIN_LENGTH_DEFAULT,
  TREES_INTERNAL_RADIUS_DEFAULT,
} from 'constants/geodata';

export type CustomPolygon = {
  id: number;
  name: string;
  polygon: unknown[];
};

export type GeodataTarget = {
  id?: number | string;
  type?: string;
  name?: string;
  radius?: number | string | null;
  minArea?: number | string | null;
  polygon?: unknown[] | null;
  [key: string]: unknown;
};

export interface GeodataParams {
  sizeDefinedBy: string;
  lengthMid: number;
  resolutionMid: number;
  lengthFar: number;
  resolutionFar: number;
  lakesEnabled: boolean;
  lakesRadius: number;
  riversEnabled: boolean;
  riversRadius: number;
  forestsEnabled: boolean;
  forestsRadius: number;
  customTargetsEnabled: boolean;
  customTargets: CustomPolygon[];
  targets: GeodataTarget[];
  minArea: number;
  isInCloseTerrain: boolean;
  isInIntermediateTerrain: boolean;
  isBuildingInAnalysisArea: boolean;
  buildingsAsProjectSketches: boolean;
  fileForCustomObstructions: any;
  fileForProjectSketches: any;
  treesRadius: number;
  treesMinimalHeight: number;
  treesDonutRadius: number;
  chipperCapacity: number;
  minPoints: number;
  cecimationCellEdge: number;
  treesInAnalysisArea: boolean;
  concaveAlphaValue: number | null;
  [key: string]: unknown;
}

export interface GeodataState {
  name: string;
  description: string;
  createdAt?: string | number | Date | null;
  createdBy?: string | null;
  clonedFromGeodataId: string | null;
  cloneSourceStatus?: string | null;
  cloneSourceProgressStatus?: string | null;
  cloneSourceHasTerrain?: boolean;
  cloneSourceHasBuildings?: boolean;
  cloneSourceHasTrees?: boolean;
  cloneSourceHasTargets?: boolean;
  terrainEnabled: boolean;
  buildingsEnabled: boolean;
  treesEnabled: boolean;
  params: GeodataParams;
}

export type CloneGeodataSource = {
  id?: number | string;
  name?: string | null;
  description?: string | null;
  createdAt?: string | number | Date | null;
  createdBy?: string | null;
  status?: unknown;
  progressStatus?: unknown;
  clonedFromGeodataId?: unknown;
  hasTerrain?: unknown;
  hasBuilding?: unknown;
  hasBuildings?: unknown;
  hasVegetation?: unknown;
  hasTrees?: unknown;
  hasTargets?: unknown;
  targets?: unknown;
  terrainEnabled?: unknown;
  buildingsEnabled?: unknown;
  treesEnabled?: unknown;
  terrain?: Partial<GeodataParams> & Record<string, unknown>;
  building?: Partial<GeodataParams> & Record<string, unknown>;
  vegetation?: Partial<GeodataParams> & Record<string, unknown>;
  params?: Partial<GeodataParams> & Record<string, unknown>;
  [key: string]: unknown;
};

export type CloneGeodataOptions = {
  appendClonedSuffix?: boolean;
};

export interface GeodataContextValue {
  geodata: GeodataState;
  setGeodata: Dispatch<SetStateAction<GeodataState>>;
  geodataParams: GeodataParams;
  setGeodataParams: (newParams: Partial<GeodataParams>) => void;
  clearGeodataFormData: () => void;
  clearInSwissGatheredTypeInfo: () => void;
  cloneGeodataData: (pItem?: CloneGeodataSource, options?: CloneGeodataOptions) => void;
  isCloningGeodata: boolean;
  setIsCloningGeodata: Dispatch<SetStateAction<boolean>>;
}

export const GeodataContext = createContext<GeodataContextValue>({} as GeodataContextValue);

interface GeodataContextProviderProps {
  children: ReactNode;
}

const normalizeCustomPolygon = (
  polygon: Partial<GeodataTarget> = {},
  fallbackId = 1,
  fallbackName = 'Polygon 1'
): CustomPolygon => ({
  id: Number.isFinite(Number(polygon?.id)) ? Number(polygon.id) : fallbackId,
  name: (polygon?.name as string) || fallbackName,
  polygon: Array.isArray(polygon?.polygon) ? polygon.polygon : [],
});

const defaultGeodataParams: GeodataParams = {
  sizeDefinedBy: 'ANALYSIS AREA',
  lengthMid: MID_TERRAIN_LENGTH_DEFAULT,
  resolutionMid: 5,
  lengthFar: FAR_TERRAIN_LENGTH_DEFAULT,
  resolutionFar: 180,
  lakesEnabled: false,
  lakesRadius: FAR_TERRAIN_LENGTH_DEFAULT + MID_TERRAIN_LENGTH_DEFAULT,
  riversEnabled: false,
  riversRadius: 1000,
  forestsEnabled: false,
  forestsRadius: 2500,
  customTargetsEnabled: false,
  customTargets: [],
  targets: [],
  minArea: 1000,
  isInCloseTerrain: true,
  isInIntermediateTerrain: true,
  isBuildingInAnalysisArea: false,
  buildingsAsProjectSketches: false,
  fileForCustomObstructions: null,
  fileForProjectSketches: null,
  treesRadius: 200,
  treesMinimalHeight: 3,
  treesDonutRadius: TREES_INTERNAL_RADIUS_DEFAULT,
  chipperCapacity: 7500,
  minPoints: 100,
  cecimationCellEdge: 0.75,
  treesInAnalysisArea: false,
  concaveAlphaValue: 2,
};

const mergeTargetUiFieldsFromTargets = (params: Partial<GeodataParams> = {}): GeodataParams => {
  const targets = Array.isArray(params?.targets) ? params.targets : [];
  const lakesTarget = targets.find((target) => target?.type === 'LAKE');
  const riversTarget = targets.find((target) => target?.type === 'RIVER');
  const forestsTarget = targets.find((target) => target?.type === 'FOREST');
  const lakeRadius = getTargetRadiusMax(
    Number(params.lengthMid ?? defaultGeodataParams.lengthMid),
    Number(params.lengthFar ?? defaultGeodataParams.lengthFar)
  );
  const customTargets = targets
    .filter((target) => target?.type === 'CUSTOM')
    .map((target, index) =>
      normalizeCustomPolygon(
        {
          id: target?.id,
          name: target?.name,
          polygon: Array.isArray(target?.polygon?.[0]) ? target.polygon[0] : [],
        },
        index + 1,
        `Polygon ${index + 1}`
      )
    );

  return {
    ...defaultGeodataParams,
    ...params,
    targets,
    lakesEnabled: Boolean(lakesTarget),
    lakesRadius: Number.isFinite(Number(lakesTarget?.radius)) ? Number(lakesTarget?.radius) : lakeRadius,
    riversEnabled: Boolean(riversTarget),
    riversRadius: Number.isFinite(Number(riversTarget?.radius)) ? Number(riversTarget?.radius) : 1000,
    forestsEnabled: Boolean(forestsTarget),
    forestsRadius: Number.isFinite(Number(forestsTarget?.radius)) ? Number(forestsTarget?.radius) : 2500,
    minArea: Number.isFinite(Number(forestsTarget?.minArea))
      ? Number(forestsTarget?.minArea)
      : Number.isFinite(Number(params?.minArea))
        ? Number(params.minArea)
        : 1000,
    customTargetsEnabled: customTargets.length > 0,
    customTargets,
  };
};

export const normalizeGeodataParams = (params: Partial<GeodataParams> = {}): GeodataParams => {
  const nextTargets = Array.isArray(params?.targets) ? params.targets : [];
  return mergeTargetUiFieldsFromTargets({
    ...params,
    targets: nextTargets,
  });
};

const geodataData: GeodataState = {
  name: '',
  description: '',
  createdAt: null,
  createdBy: null,
  clonedFromGeodataId: null,
  cloneSourceStatus: null,
  cloneSourceProgressStatus: null,
  cloneSourceHasTerrain: false,
  cloneSourceHasBuildings: false,
  cloneSourceHasTrees: false,
  cloneSourceHasTargets: false,
  terrainEnabled: true,
  buildingsEnabled: true,
  treesEnabled: true,

  params: {
    sizeDefinedBy: 'ANALYSIS AREA',
    lengthMid: MID_TERRAIN_LENGTH_DEFAULT,
    resolutionMid: 5,
    lengthFar: FAR_TERRAIN_LENGTH_DEFAULT,
    resolutionFar: 180,

    lakesEnabled: false,
    lakesRadius: FAR_TERRAIN_LENGTH_DEFAULT + MID_TERRAIN_LENGTH_DEFAULT,
    riversEnabled: false,
    riversRadius: 1000,
    forestsEnabled: false,
    forestsRadius: 2500,
    customTargetsEnabled: false,
    customTargets: [],
    targets: [],
    minArea: 1000,

    isInCloseTerrain: true,
    isInIntermediateTerrain: true,
    isBuildingInAnalysisArea: false,
    buildingsAsProjectSketches: false,
    fileForCustomObstructions: null,
    fileForProjectSketches: null,

    treesRadius: 200,
    treesMinimalHeight: 3,
    treesDonutRadius: TREES_INTERNAL_RADIUS_DEFAULT,
    chipperCapacity: 7500,
    minPoints: 100,
    cecimationCellEdge: 0.75,
    treesInAnalysisArea: false,
    concaveAlphaValue: 2,
  },
};

const GeodataContextProvider = (props: GeodataContextProviderProps) => {
  const [isCloningGeodata, setIsCloningGeodata] = useState(false);
  const [geodata, setGeodata] = useState<GeodataState>(geodataData);

  const clearInSwissGatheredTypeInfo = useCallback(() => {
    setGeodata((prevState) => ({
      ...prevState,
      params: {
        ...prevState.params,
        sizeDefinedBy: 'ANALYSIS AREA',
        lengthMid: 500,
        resolutionMid: 5,
        lengthFar: 5000,
        resolutionFar: 180,
      },
    }));
  }, []);

  const clearGeodataFormData = useCallback(() => {
    setGeodata(geodataData);
  }, []);

  const setGeodataParams = useCallback((newParams: Partial<GeodataParams>) => {
    setGeodata((prevState) => ({
      ...prevState,
      params: normalizeGeodataParams({
        ...prevState.params,
        ...newParams,
      }),
    }));
  }, []);

  const cloneGeodataData = useCallback((pItem: CloneGeodataSource = {}, options: CloneGeodataOptions = {}) => {
    const { appendClonedSuffix = true } = options;
    const params: Partial<GeodataParams> & Record<string, unknown> = {
      ...(pItem?.params ?? {}),
      ...(pItem?.terrain ?? {}),
      ...(pItem?.building ?? {}),
      ...(pItem?.vegetation ?? {}),
    };
    const targets = Array.isArray(params?.targets) ? params.targets : [];
    const parseBooleanFlag = (value: unknown, fallback = false): boolean => {
      if (value === undefined || value === null) return fallback;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value === 1;
      if (typeof value === 'string') {
        if (value === '1' || value.toLowerCase() === 'true') return true;
        if (value === '0' || value.toLowerCase() === 'false') return false;
      }
      return Boolean(value);
    };
    const terrainEnabled = parseBooleanFlag(pItem?.terrainEnabled ?? pItem?.terrain, false);
    const buildingsEnabled = parseBooleanFlag(pItem?.buildingsEnabled ?? pItem?.building, false);
    const treesEnabled = parseBooleanFlag(pItem?.treesEnabled ?? pItem?.vegetation, false);
    const cloneSourceHasTerrain = parseBooleanFlag(pItem?.hasTerrain ?? pItem?.terrainEnabled ?? pItem?.terrain, false);
    const cloneSourceHasBuildings = parseBooleanFlag(
      pItem?.hasBuilding ?? pItem?.hasBuildings ?? pItem?.buildingsEnabled ?? pItem?.building,
      false
    );
    const cloneSourceHasTrees = parseBooleanFlag(
      pItem?.hasVegetation ?? pItem?.hasTrees ?? pItem?.treesEnabled ?? pItem?.vegetation,
      false
    );
    const cloneSourceHasTargets =
      parseBooleanFlag(pItem?.hasTargets, false) ||
      targets.some((target) => target != null) ||
      parseBooleanFlag((pItem?.params as { targets?: unknown } | undefined)?.targets, false);

    const clonedGeodataData: GeodataState = {
      name: pItem?.name ? `${pItem.name}${appendClonedSuffix ? ' cloned' : ''}` : '',
      description: pItem?.description || '',
      createdAt: pItem?.createdAt ?? null,
      createdBy: pItem?.createdBy ?? null,
      clonedFromGeodataId: pItem?.id == null ? null : String(pItem.id),
      cloneSourceStatus: pItem?.status == null ? null : String(pItem.status),
      cloneSourceProgressStatus: pItem?.progressStatus == null ? null : String(pItem.progressStatus),
      cloneSourceHasTerrain,
      cloneSourceHasBuildings,
      cloneSourceHasTrees,
      cloneSourceHasTargets,
      terrainEnabled,
      buildingsEnabled,
      treesEnabled,
      params: normalizeGeodataParams({
        sizeDefinedBy: (params?.sizeDefinedBy as string) || 'ANALYSIS AREA',
        lengthMid: parseFloat(String(params?.lengthMid ?? 500)),
        resolutionMid: parseFloat(String(params?.resolutionMid ?? 5)),
        lengthFar: parseFloat(String(params?.lengthFar ?? 5000)),
        resolutionFar: parseFloat(String(params?.resolutionFar ?? 180)),
        minArea: parseFloat(String(params?.minArea ?? 1000)),
        targets,
        isInCloseTerrain: parseBooleanFlag(params?.isInCloseTerrain, true),
        isInIntermediateTerrain: parseBooleanFlag(params?.isInIntermediateTerrain, true),
        isBuildingInAnalysisArea: parseBooleanFlag(params?.isBuildingInAnalysisArea, false),
        buildingsAsProjectSketches: parseBooleanFlag(params?.buildingsAsProjectSketches, false),
        fileForCustomObstructions: params?.customObstructionFile ?? null,
        fileForProjectSketches: params?.projectSketchesFile ?? null,
        treesRadius: parseFloat(String(params?.treesRadius ?? 200)),
        treesMinimalHeight: parseFloat(String(params?.treesMinimalHeight ?? 3)),
        treesDonutRadius: parseFloat(String(params?.treesDonutRadius ?? TREES_INTERNAL_RADIUS_DEFAULT)),
        chipperCapacity: parseFloat(String(params?.chipperCapacity ?? 7500)),
        minPoints: parseInt(String(params?.minPoints ?? 100)),
        cecimationCellEdge: parseFloat(String(params?.cecimationCellEdge ?? 0.75)),
        treesInAnalysisArea: Boolean(params?.treesInAnalysisArea ?? false),
        concaveAlphaValue: params?.concaveAlphaValue == null ? null : parseFloat(String(params.concaveAlphaValue)),
      }),
    };

    setGeodata(clonedGeodataData);
  }, []);

  return (
    <GeodataContext.Provider
      value={{
        geodata,
        setGeodata,
        geodataParams: geodata.params,
        setGeodataParams,
        clearGeodataFormData,
        clearInSwissGatheredTypeInfo,
        cloneGeodataData,
        isCloningGeodata,
        setIsCloningGeodata,
      }}
    >
      {props.children}
    </GeodataContext.Provider>
  );
};

export default GeodataContextProvider;
